"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Search, FileText, Palette, PenTool, Sparkles, Shield, Eye,
  Loader2, Check, Clock, AlertTriangle, ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { PageBackground } from "@/components/ui/page-background";
import { HeartbeatPulse } from "@/components/shared/HeartbeatPulse";
import { urls } from "@/lib/urls";

/* ─── Step definitions per type ─── */

interface StepDef {
  id: string;
  icon: typeof Search;
  agent: string;
  activeText: string;
  doneText: string;
  flavor: string[];
}

const PLAN_STEPS: StepDef[] = [
  {
    id: "market_complete",
    icon: Search,
    agent: "Market Research",
    activeText: "Analyst is researching your market...",
    doneText: "Market data collected",
    flavor: [
      "Scanning competitor pricing and positioning",
      "Analyzing market size and growth trends",
      "Identifying your target customer segments",
    ],
  },
  {
    id: "strategy_tactics_complete",
    icon: Shield,
    agent: "Strategy & Tactics",
    activeText: "Building your competitive strategy...",
    doneText: "Strategy and tactics ready",
    flavor: [
      "Crafting your unique value proposition",
      "Mapping revenue streams and pricing",
      "Designing your go-to-market playbook",
    ],
  },
  {
    id: "skeleton_complete",
    icon: FileText,
    agent: "Plan Architecture",
    activeText: "Structuring the full business plan...",
    doneText: "Plan skeleton built",
    flavor: [
      "Organizing insights into a coherent narrative",
      "Building financial projections framework",
      "Connecting market data to strategy",
    ],
  },
  {
    id: "sections_complete",
    icon: PenTool,
    agent: "Strategist Writing",
    activeText: "Writing 7 detailed sections...",
    doneText: "All sections written",
    flavor: [
      "Writing Problem & Customer analysis",
      "Crafting your Revenue Model",
      "Building the 90-Day Playbook",
      "Calculating unit economics",
      "Writing competitive analysis",
    ],
  },
  {
    id: "complete",
    icon: Eye,
    agent: "Quality Review",
    activeText: "Reviewer auditing the full plan...",
    doneText: "Business plan complete!",
    flavor: [
      "Checking numbers against market data",
      "Verifying strategic consistency",
      "Final polish and formatting",
    ],
  },
];

const BRAND_STEPS: StepDef[] = [
  {
    id: "foundation_complete",
    icon: Search,
    agent: "Brand Foundation",
    activeText: "Analyzing your market positioning...",
    doneText: "Brand foundation set",
    flavor: [
      "Defining your brand archetype",
      "Crafting your positioning statement",
      "Building your elevator pitch",
    ],
  },
  {
    id: "names_voice_complete",
    icon: Sparkles,
    agent: "Names & Voice",
    activeText: "Generating brand names and voice...",
    doneText: "Names and voice defined",
    flavor: [
      "Brainstorming 5 unique brand names",
      "Checking domain availability via WHOIS",
      "Crafting your brand voice framework",
    ],
  },
  {
    id: "messaging_complete",
    icon: PenTool,
    agent: "Brand Messaging",
    activeText: "Writing taglines and messaging...",
    doneText: "Messaging framework ready",
    flavor: [
      "Mapping competitor positioning gaps",
      "Writing persona-specific messaging",
      "Crafting memorable taglines",
    ],
  },
  {
    id: "strategist_complete",
    icon: Shield,
    agent: "Strategy Assembly",
    activeText: "Assembling full brand strategy...",
    doneText: "Brand strategy complete",
    flavor: [
      "Stress-testing brand differentiation",
      "Connecting voice to visual direction",
      "Preparing designer handoff brief",
    ],
  },
  {
    id: "designer_complete",
    icon: Palette,
    agent: "Visual Designer",
    activeText: "Designing colors, fonts, visuals...",
    doneText: "Visual identity designed",
    flavor: [
      "Selecting color palettes for your archetype",
      "Pairing fonts for headline and body",
      "Designing logo concepts and mockups",
    ],
  },
  {
    id: "complete",
    icon: Check,
    agent: "Post-Processing",
    activeText: "Checking social handles and domains...",
    doneText: "Brand kit complete!",
    flavor: [
      "Verifying social media handle availability",
      "Finalizing brand guidelines document",
      "Packaging your complete brand kit",
    ],
  },
];

const LANDING_STEPS: StepDef[] = [
  {
    id: "generating",
    icon: PenTool,
    agent: "Landing Builder",
    activeText: "Writing conversion copy and page structure...",
    doneText: "Landing page built",
    flavor: [
      "Analyzing your brand and marketing data",
      "Writing headline and hero section",
      "Adding social proof and testimonials",
      "Optimizing call-to-action",
      "Finalizing responsive layout",
    ],
  },
  {
    id: "complete",
    icon: Check,
    agent: "Finalization",
    activeText: "Applying brand colors and fonts...",
    doneText: "Landing page complete!",
    flavor: [
      "Styling with your brand identity",
      "Final quality check",
    ],
  },
];

const STEP_MAP: Record<string, StepDef[]> = {
  plan: PLAN_STEPS,
  brand: BRAND_STEPS,
  landing: LANDING_STEPS,
};

const TITLES: Record<string, { title: string; subtitle: string }> = {
  plan: { title: "Building Your Business Plan", subtitle: "Three AI agents are researching, strategizing, and writing your plan" },
  brand: { title: "Creating Your Brand Kit", subtitle: "Brand strategist and designer are building your identity" },
  landing: { title: "Building Your Landing Page", subtitle: "AI is crafting conversion-optimized copy with your brand identity" },
};

const TIPS: Record<string, string[]> = {
  plan: [
    "Your plan includes market research with real web data",
    "Three agents collaborate: Analyst, Strategist, and Reviewer",
    "Each section is backed by evidence, not guesses",
    "The Reviewer independently audits every claim",
    "Your plan is built to convince investors, not just look pretty",
  ],
  brand: [
    "Your brand is stress-tested against competitors",
    "Domain availability is checked via real WHOIS lookups",
    "Color palettes are matched to your brand archetype",
    "Social media handles are verified for availability",
    "Two specialized agents: Strategist (Mira) and Designer (Kai)",
  ],
  landing: [
    "Copy is based on your business plan insights",
    "Headlines are optimized for conversion",
    "Brand colors and fonts are applied automatically",
    "Social proof elements boost credibility",
  ],
};

/* ─── Elapsed time (same pattern as LiveGenerationJourney) ─── */

function useElapsed(startIso: string | null, active: boolean): string {
  const [nowMs, setNowMs] = useState<number | null>(null);
  useEffect(() => {
    if (!startIso) {
      setNowMs(null);
      return;
    }
    setNowMs(Date.now());
    if (!active) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active, startIso]);

  if (!startIso || nowMs === null) return "";
  const seconds = Math.max(0, Math.floor((nowMs - new Date(startIso).getTime()) / 1000));
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
}

/* ─── Component ─── */

interface Props {
  itemId: string;
  sessionId: string;
  type: "plan" | "brand" | "landing";
  pollUrl: string;
  failed?: boolean;
}

export function LiveArtifactJourney({ itemId, sessionId, type, pollUrl, failed }: Props) {
  const router = useRouter();
  const steps = STEP_MAP[type] ?? PLAN_STEPS;
  const title = TITLES[type] ?? TITLES.plan;
  const tips = TIPS[type] ?? TIPS.plan;

  const [step, setStep] = useState<string | null>(null);
  const [stepAt, setStepAt] = useState<string | null>(null);
  const [heartbeat, setHeartbeat] = useState<string | null>(null);
  const [tipIdx, setTipIdx] = useState(0);
  const [flavorIdx, setFlavorIdx] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Poll status
  useEffect(() => {
    if (failed || !pollUrl) return;
    const handleData = (data: Record<string, unknown>) => {
      if (data.step) setStep(data.step as string);
      if (data.stepAt) setStepAt(data.stepAt as string);
      if (data.heartbeat) setHeartbeat(data.heartbeat as string);
    };

    // Initial fetch
    // intentional: prefetch live status; subsequent 4s poll covers it on transient failure
    fetch(pollUrl).then(r => r.ok ? r.json() : null).then(d => { if (d) handleData(d); }).catch(() => {});

    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(pollUrl);
        if (!res.ok) return;
        const data = await res.json();
        handleData(data);
        if (data.status === "complete" || data.status === "deployed") {
          if (pollRef.current) clearInterval(pollRef.current);
          router.refresh();
        } else if (data.status === "failed" || data.status === "error") {
          if (pollRef.current) clearInterval(pollRef.current);
          router.refresh();
        }
      // intentional: 4s status poll; transient failure recovers next tick
      } catch {}
    }, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pollUrl, failed, router]);

  // Rotate tips
  useEffect(() => {
    const id = setInterval(() => setTipIdx((i) => (i + 1) % tips.length), 8000);
    return () => clearInterval(id);
  }, [tips.length]);

  // Rotate flavor
  useEffect(() => {
    const id = setInterval(() => setFlavorIdx((i) => i + 1), 4000);
    return () => clearInterval(id);
  }, []);

  // Failed state
  if (failed) {
    return (
      <div className="relative min-h-screen bg-surface">
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-4">
          <div className="w-full max-w-lg space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500/20">
                <AlertTriangle className="size-4 text-red-400" />
              </div>
              <div className="rounded-2xl rounded-tl-sm border border-subtle bg-surface-card px-5 py-4">
                <p className="text-sm text-[#A8A29E] leading-relaxed">
                  Generation failed. Your credits were not charged. Try again?
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 pt-2">
              <Link
                href={`/dashboard?session=${sessionId}`}
                className="inline-flex items-center gap-1.5 rounded-xl border border-subtle bg-white/[0.04] px-4 py-2 text-sm text-white/70 hover:bg-white/[0.08]"
              >
                <ArrowLeft className="size-3.5" />
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Find current step index
  const currentStepIdx = step
    ? (() => {
        // For section steps like "section_problem_customer", map to "sections_complete" (in progress)
        if (type === "plan" && step.startsWith("section_")) return 3; // sections step
        const idx = steps.findIndex((s) => s.id === step);
        // If step is a "complete" marker, the NEXT step is active
        if (idx >= 0 && step.endsWith("_complete")) return idx + 1;
        if (idx >= 0) return idx;
        return 0;
      })()
    : 0;

  return (
    <div className="relative min-h-screen bg-surface">
      <PageBackground />
      <div className="relative mx-auto max-w-2xl px-4 pb-32 pt-16 md:pt-24">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-2xl font-bold text-light md:text-3xl">
            {title.title}
          </h1>
          <p className="mt-2 text-sm text-dim max-w-md mx-auto">
            {title.subtitle}
          </p>
          {/* Heartbeat pulse */}
          <div className="flex justify-center mt-3">
            <HeartbeatPulse heartbeatIso={heartbeat} />
          </div>
        </div>

        {/* Step cards */}
        <div className="space-y-3">
          {steps.map((s, i) => {
            const isDone = i < currentStepIdx;
            const isActive = i === currentStepIdx;
            const isNext = i > currentStepIdx;

            return (
              <ArtifactStepCard
                key={s.id}
                def={s}
                isDone={isDone}
                isActive={isActive}
                isNext={isNext}
                stepAt={isActive ? stepAt : null}
                flavorIdx={flavorIdx}
              />
            );
          })}
        </div>

        {/* Tip */}
        <div className="mt-6 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-center">
          <p className="text-xs text-white/30">{tips[tipIdx]}</p>
        </div>

        {/* Back link */}
        <div className="mt-6 text-center">
          <Link
            href={urls.niche({ id: sessionId, slug: sessionId })}
            className="text-sm text-white/30 hover:text-white/50 transition-colors"
          >
            Back to your startup — result saves automatically
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─── Step Card ─── */

function ArtifactStepCard({ def, isDone, isActive, isNext, stepAt, flavorIdx }: {
  def: StepDef;
  isDone: boolean;
  isActive: boolean;
  isNext: boolean;
  stepAt: string | null;
  flavorIdx: number;
}) {
  const Icon = def.icon;
  const elapsed = useElapsed(stepAt, isActive);
  const currentFlavor = def.flavor[flavorIdx % def.flavor.length];

  return (
    <div
      className={cn(
        "rounded-xl border p-4 transition-all duration-500",
        isDone && "border-emerald-500/20 bg-emerald-500/[0.04]",
        isActive && "border-amber-500/30 bg-amber-500/[0.06] shadow-lg shadow-amber-500/5",
        isNext && "border-white/[0.05] bg-white/[0.01] opacity-40"
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            isDone && "bg-emerald-500/15",
            isActive && "bg-amber-500/15",
            isNext && "bg-white/[0.04]"
          )}
        >
          {isDone ? (
            <Check className="size-5 text-emerald-400" />
          ) : isActive ? (
            <Loader2 className="size-5 text-amber-400 animate-spin" />
          ) : (
            <Icon className="size-5 text-white/20" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              "text-sm font-semibold",
              isDone && "text-emerald-400",
              isActive && "text-amber-400",
              isNext && "text-white/30"
            )}>
              {def.agent}
            </span>
            {isDone && (
              <span className="rounded-full bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 text-[9px] font-bold text-emerald-400">
                Done
              </span>
            )}
          </div>
          <p className={cn(
            "text-xs mt-0.5",
            isDone && "text-emerald-400/50",
            isActive && "text-white/50",
            isNext && "text-white/15"
          )}>
            {isDone ? def.doneText : isActive ? currentFlavor : def.activeText}
          </p>
        </div>

        {/* Timer */}
        {isActive && elapsed && (
          <div className="flex items-center gap-1 shrink-0">
            <Clock className="size-3 text-amber-400/50" />
            <span className="text-xs font-mono text-amber-400/60 tabular-nums">{elapsed}</span>
          </div>
        )}
      </div>
    </div>
  );
}
